<script setup lang="ts">
import { mainNavigationQaEquipment, mainNavigationMedicalSystems } from '@/components/icons/icons';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { translateGroupName, truthy } from '@/utils/helper';
import SetupParameter from '@/views/tasks/components/SetupParameter.vue';
import { groupBy } from 'lodash';
import { computed, useTemplateRef } from 'vue';
import { useAlignedTargetWidth } from './useAlignedTargetWidth';
import { ParameterCombinationTitleValues } from './useTaskStepTitles';

const props = defineProps<{
  combination: ParameterCombinationTitleValues;
  mergeSets?: boolean;
  targetName?: string;
}>();

const targetValues = useTemplateRef<HTMLElement>('targetValues');
const alignedWidth = useAlignedTargetWidth(targetValues, () => !!props.mergeSets);

const target = computed(() => props.combination?.target?.filter(truthy));

const support = computed(() => groupBy(props.combination?.support?.filter(truthy), 'groupName'));

const supportGroups = computed(() =>
  Object.entries(support.value)
    .filter(([, parameters]) => parameters?.length)
    .map(([groupName, parameters]) => ({ groupName, parameters })),
);

const isEmpty = computed(
  () => !props.combination?.target?.length && !props.combination?.support?.length,
);

function showFullText(event: MouseEvent) {
  const element = event.currentTarget as HTMLElement;
  element.title = element.innerText;
}
</script>

<template>
  <span v-if="isEmpty">
    {{ $t('Workstep') }}
  </span>

  <div v-else-if="mergeSets" class="d-flex align-center merged-sets">
    <div class="d-flex align-center flex-shrink-0">
      <v-icon
        v-if="target?.length"
        color="secondary"
        :icon="mainNavigationMedicalSystems"
        class="mr-2"
      />
      <div
        class="d-block text-no-wrap"
        :style="alignedWidth ? { minWidth: `${alignedWidth}px` } : undefined"
      >
        <div ref="targetValues" class="d-inline-block">
          <div
            v-if="targetName"
            class="merged-caption text-caption font-weight-medium text-primary-lighten-1"
          >
            {{ targetName }}
          </div>
          <span class="text-body-2 font-weight-semibold text-primary-darken-2">
            <template v-for="(item, index) in target" :key="index">
              <span v-if="index !== 0"> | </span>
              <span class="font-weight-semibold"><SetupParameter :item="item" /></span>
            </template>
          </span>
        </div>
      </div>
    </div>

    <template v-if="supportGroups.length">
      <v-icon color="secondary" :icon="mainNavigationQaEquipment" class="mr-2 ml-6 flex-shrink-0" />
      <div class="d-flex ga-6 py-2 merged-lines">
        <div v-for="group in supportGroups" :key="group.groupName" class="merged-group">
          <div
            v-if="group.groupName !== 'undefined'"
            class="merged-caption text-caption font-weight-medium text-primary-lighten-1"
          >
            {{ translateGroupName(group.groupName) }}
          </div>
          <div class="merged-line" @mouseenter="showFullText">
            <template v-for="(item, index) in group.parameters" :key="index">
              <span v-if="index !== 0"> | </span>
              <span class="text-body-2 font-weight-semibold text-primary-darken-2"
                ><SetupParameter :item="item"
              /></span>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>

  <div v-else class="d-flex align-center">
    <v-icon
      v-if="target?.length"
      color="secondary"
      :icon="mainNavigationMedicalSystems"
      class="mr-2"
    />

    <template v-for="(item, index) in target" :key="index">
      <span v-if="index !== 0" class="ml-1 mr-1"> | </span>
      <SetupParameter :item="item" />
    </template>

    <template v-for="(parameters, groupName) in support" :key="groupName">
      <Tooltip v-if="parameters?.length" position="top" :showPointer="true">
        <template #target="{ props: tooltipProps }">
          <v-icon
            v-bind="tooltipProps"
            color="secondary"
            :icon="mainNavigationQaEquipment"
            class="mr-2 ml-6"
          />
        </template>
        <template #tooltip>
          {{ groupName }}
        </template>
      </Tooltip>

      <template v-for="(item, index) in parameters" :key="index">
        <span v-if="index !== 0"> | </span>
        <SetupParameter :item="item" />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
// SetupParameter's min-width only leaves gaps in the stacked lines; `d-block` keeps its own.
.merged-sets :deep(span[style*='min-width']:not(.d-block)) {
  min-width: 0 !important;
}

.merged-sets {
  min-width: 0;
}

.merged-lines {
  min-width: 0;
}

.merged-group {
  min-width: 0;
}

.merged-caption {
  line-height: 1.4;
}

.merged-line {
  min-width: 0;
  // The panel title sets line-height: 1; with overflow hidden that cuts off descenders ("g").
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
